<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { provinceOptions, countryValue, splitCountryValue } from '../lib/canadaTax.js';
  import {
    filterSalesAccounts,
    filterExpenseAccounts,
    formatAccountOptionLabel,
    loadXeroChartAccounts
  } from '../lib/settings/xeroAccountFilters.js';

  const dispatch = createEventDispatcher();

  let currentStep = 0;
  let closing = false;
  let saving = false;
  let error = null;
  let loadingSettings = true;

  // Accounting platform decision (BZ-32): 'xero' (full Xero sync) or 'quickbooks' (JobTread-only).
  let platform = 'xero';

  // Step list is platform-aware: QuickBooks users skip the Xero default-accounts step.
  $: steps = platform === 'quickbooks'
    ? ['welcome', 'platform', 'jobtread', 'complete']
    : ['welcome', 'platform', 'jobtread', 'xero', 'complete'];
  $: stepKey = steps[currentStep];

  // Page 2: JobTread state
  let jobtreadApiKey = '';
  let jobtreadOrgId = '';
  let jobtreadOrgName = '';
  let jobtreadHasExistingKey = false;
  let availableOrganizations = [];
  let fetchingOrganizations = false;
  let showManualOrgId = false;
  let jobtreadConnectionStatus = null;
  // When the JobTread org is already claimed by another Buildzilla user, we surface
  // a dedicated message (below the connection result) plus an exit option, rather
  // than the generic top-of-wizard error banner.
  let orgClaimedMessage = null;

  // Page 3: Xero state
  let xeroInvoiceCode = '';
  let xeroExpenseCode = '';
  // Picker value: a country code, or 'CA-BC' etc. for a Canadian province.
  let country = 'AU';
  const caProvinceOptions = provinceOptions();
  let xeroConnected = false;
  let xeroAccounts = [];
  let loadingAccounts = false;
  let accountsLoadAttempted = false;

  $: jobtreadValid = jobtreadConnectionStatus === 'success' || (jobtreadHasExistingKey && jobtreadOrgId.trim() !== '');
  $: xeroValid = xeroInvoiceCode.trim() !== '' && xeroExpenseCode.trim() !== '' && country.trim() !== '';
  $: salesAccounts = filterSalesAccounts(xeroAccounts);
  $: expenseAccounts = filterExpenseAccounts(xeroAccounts);
  $: salesCodeInList = salesAccounts.some((a) => String(a.code) === String(xeroInvoiceCode.trim()));
  $: expenseCodeInList = expenseAccounts.some((a) => String(a.code) === String(xeroExpenseCode.trim()));
  $: useAccountDropdowns = xeroConnected && xeroAccounts.length > 0;

  // When the user reaches the Xero step, try loading CoA if already connected.
  $: if (stepKey === 'xero' && !accountsLoadAttempted && !loadingAccounts) {
    prepareXeroAccountDropdowns();
  }

  onMount(async () => {
    try {
      const response = await fetch('/api/users/integrations');
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const jtKey = data.integrations.jobtread.apiKey === 'BLANK' ? '' : (data.integrations.jobtread.apiKey || '');
          const jtOrgId = data.integrations.jobtread.orgId === 'BLANK' ? '' : (data.integrations.jobtread.orgId || '');
          jobtreadApiKey = jtKey;
          jobtreadOrgId = jtOrgId;
          jobtreadHasExistingKey = data.integrations.jobtread.hasApiKey || false;
          if (jtOrgId) {
            showManualOrgId = true;
          }

          const inv = data.integrations.xero.invoiceCode || '';
          const exp = data.integrations.xero.expenseCode || '';
          xeroInvoiceCode = inv === 'BLANK' ? '' : inv;
          xeroExpenseCode = exp === 'BLANK' ? '' : exp;
          country = countryValue(data.country || 'AU', data.province);
          platform = data.accountingPlatform === 'quickbooks' ? 'quickbooks' : 'xero';
        }
      }
    } catch (err) {
      console.error('Failed to load existing settings for onboarding:', err);
    } finally {
      loadingSettings = false;
    }
  });

  async function prepareXeroAccountDropdowns() {
    accountsLoadAttempted = true;
    loadingAccounts = true;
    try {
      const authResponse = await fetch('/api/xero/check-auth');
      const authData = await authResponse.json();
      xeroConnected = authResponse.ok && authData.authenticated === true;
      if (!xeroConnected) {
        xeroAccounts = [];
        return;
      }
      const { accounts } = await loadXeroChartAccounts();
      xeroAccounts = accounts;
    } catch (err) {
      console.error('Failed to prepare Xero account dropdowns:', err);
      xeroConnected = false;
      xeroAccounts = [];
    } finally {
      loadingAccounts = false;
    }
  }

  // --- Navigation ---

  function nextStep() {
    error = null;
    if (currentStep < steps.length - 1) {
      currentStep++;
    }
  }

  function prevStep() {
    error = null;
    if (currentStep > 0) {
      currentStep--;
    }
  }

  // --- Page 2 logic (JobTread) ---

  async function fetchOrganizations() {
    if (!jobtreadApiKey.trim()) {
      error = 'Please enter a JobTread Grant Key first.';
      return;
    }
    fetchingOrganizations = true;
    error = null;
    availableOrganizations = [];

    try {
      const response = await fetch('/api/users/fetch-jobtread-organizations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ apiKey: jobtreadApiKey })
      });
      const data = await response.json();

      if (response.ok && data.success) {
        availableOrganizations = data.organizations;
        if (availableOrganizations.length === 1) {
          jobtreadOrgId = availableOrganizations[0].id;
          jobtreadOrgName = availableOrganizations[0].name;
        } else if (availableOrganizations.length === 0) {
          error = 'No organizations found for this API key.';
          showManualOrgId = true;
        }
      } else {
        error = data.error || 'Failed to fetch organizations';
        showManualOrgId = true;
      }
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
      error = 'Failed to fetch organizations. You can enter the Organisation ID manually.';
      showManualOrgId = true;
    } finally {
      fetchingOrganizations = false;
    }
  }

  async function checkJobTreadConnection() {
    if (!jobtreadApiKey.trim() || !jobtreadOrgId.trim()) {
      error = 'Please enter both the Grant Key and select an Organisation.';
      return;
    }
    jobtreadConnectionStatus = 'checking';
    error = null;
    orgClaimedMessage = null;

    try {
      const response = await fetch('/api/users/validate-jobtread-self', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ apiKey: jobtreadApiKey, orgId: jobtreadOrgId })
      });
      const data = await response.json();

      if (response.ok && data.success) {
        jobtreadConnectionStatus = 'success';
        jobtreadOrgName = data.organization.name;
      } else if (response.status === 409) {
        // Org already claimed by another user. Show the guidance inline (below the
        // connection result) and offer an exit instead of the top error banner.
        jobtreadConnectionStatus = 'error';
        orgClaimedMessage = data.error || 'This JobTread organisation is already connected to another Buildzilla user.';
      } else {
        jobtreadConnectionStatus = 'error';
        error = data.error || 'Failed to connect to JobTread';
      }
    } catch (err) {
      console.error('JobTread connection check failed:', err);
      jobtreadConnectionStatus = 'error';
      error = 'Failed to check JobTread connection';
    }
  }

  async function saveJobTreadSettings() {
    saving = true;
    error = null;
    try {
      const response = await fetch('/api/users/integrations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jobtreadApiKey: jobtreadApiKey.trim() || 'BLANK',
          jobtreadOrgId: jobtreadOrgId.trim() || 'BLANK',
          xeroInvoiceCode: xeroInvoiceCode || 'BLANK',
          xeroExpenseCode: xeroExpenseCode || 'BLANK',
          ...splitCountryValue(country || 'AU'),
          accountingPlatform: platform
        })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to save JobTread settings');
      }
      nextStep();
    } catch (err) {
      console.error('Failed to save JobTread settings:', err);
      error = err.message || 'Failed to save settings';
    } finally {
      saving = false;
    }
  }

  // --- Page 3 logic (Xero) ---

  async function saveXeroSettings() {
    saving = true;
    error = null;
    try {
      const response = await fetch('/api/users/integrations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jobtreadApiKey: jobtreadApiKey.trim() || 'BLANK',
          jobtreadOrgId: jobtreadOrgId.trim() || 'BLANK',
          xeroInvoiceCode,
          xeroExpenseCode,
          ...splitCountryValue(country),
          accountingPlatform: platform
        })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to save Xero settings');
      }
      nextStep();
    } catch (err) {
      console.error('Failed to save Xero settings:', err);
      error = err.message || 'Failed to save settings';
    } finally {
      saving = false;
    }
  }

  // --- Page 4 logic (Complete) ---

  async function completeOnboarding() {
    closing = true;
    try {
      await fetch('/api/users/onboarding-wizard-seen', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' }
      });
    } catch (err) {
      console.error('Failed to mark onboarding as seen:', err);
    }
    // QuickBooks users never connect Xero — drop them straight into Direct Bill Import.
    // Xero users continue to the Xero OAuth flow. Full navigation ensures Layout reloads
    // userInfo (with accountingPlatform) and applies the correct nav gating.
    window.location.href = platform === 'quickbooks' ? '/direct-bill-import' : '/api/xero/login';
  }

  async function quitOnboarding() {
    closing = true;
    try {
      await fetch('/api/users/onboarding-wizard-seen', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' }
      });
    } catch (err) {
      console.error('Failed to mark onboarding as seen:', err);
    }
    dispatch('close');
  }
</script>

<!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions a11y-no-noninteractive-element-interactions -->
<div
  class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
  class:fade-out={closing}
  role="dialog"
  aria-modal="true"
  aria-label="Onboarding wizard"
>
  <div class="bg-white w-full max-w-2xl rounded-xl shadow-2xl relative animate-fade-in max-h-[90vh] overflow-y-auto">

    {#if loadingSettings}
      <div class="flex items-center justify-center py-16">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span class="ml-3 text-gray-600">Loading...</span>
      </div>
    {:else}

    <!-- Step Indicator -->
    <div class="px-6 pt-6 pb-2">
      <div class="flex items-center justify-center gap-2">
        {#each steps as _, i}
          <div class="flex items-center">
            <div
              class="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors
                {i === currentStep ? 'bg-blue-600 text-white' : i < currentStep ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'}"
            >
              {#if i < currentStep}
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              {:else}
                {i + 1}
              {/if}
            </div>
            {#if i < steps.length - 1}
              <div class="w-8 h-0.5 {i < currentStep ? 'bg-green-500' : 'bg-gray-200'}"></div>
            {/if}
          </div>
        {/each}
      </div>
    </div>

    {#if error}
      <div class="mx-6 mt-3 p-3 bg-red-50 border border-red-300 rounded-lg">
        <p class="text-sm text-red-800">{error}</p>
      </div>
    {/if}

    <!-- Step: Welcome -->
    {#if stepKey === 'welcome'}
      <div class="p-6">
        <div class="text-center mb-6">
          <div class="inline-flex items-center justify-center mb-4">
            <img src="/images/buildzilla-logo-sm.jpg" alt="Buildzilla" class="h-16 w-auto rounded-lg" />
          </div>
          <h2 class="text-2xl font-bold text-gray-900 mb-2">Welcome to Buildzilla</h2>
          <p class="text-gray-600">Your JobTread bill &amp; invoice automation partner</p>
        </div>

        <p class="text-gray-700 mb-5">
          Buildzilla provides a number of tools for your construction business, including:
        </p>

        <div class="space-y-3 mb-6">
          <div class="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
            <span class="text-blue-600 text-lg shrink-0 mt-0.5">
              <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12h16M4 6h16M4 18h16"/></svg>
            </span>
            <span class="text-sm text-gray-800">Accurately syncing your JobTread financials to your accounting system for tax purposes</span>
          </div>
          <div class="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
            <span class="text-blue-600 text-lg shrink-0 mt-0.5">
              <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            </span>
            <span class="text-sm text-gray-800">Keeping bill and invoice paid status up to date</span>
          </div>
          <div class="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
            <span class="text-blue-600 text-lg shrink-0 mt-0.5">
              <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
            </span>
            <span class="text-sm text-gray-800">Improved cost reporting insights</span>
          </div>
        </div>

        <div class="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <p class="text-sm text-gray-600">
            Buildzilla starts with you fully in control, permitting syncs one invoice at a time.
            This allows you to check whether you are happy with the way items are being synced, then increase automation once you are happy.
          </p>
        </div>

        <p class="text-gray-700 mt-5 font-medium text-center">Let's get started setting your sync up.</p>
      </div>

      <div class="px-6 pb-6 flex justify-end">
        <button
          on:click={nextStep}
          class="px-8 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium border-0"
        >
          Let's go!
        </button>
      </div>

    <!-- Step: Accounting platform choice -->
    {:else if stepKey === 'platform'}
      <div class="p-6">
        <h2 class="text-xl font-bold text-gray-900 mb-2">How do you manage your accounts?</h2>
        <p class="text-gray-600 mb-5">
          This sets up Buildzilla for the way your business works. You can change it later in Settings.
        </p>

        <div class="space-y-3">
          <button
            type="button"
            on:click={() => platform = 'xero'}
            class="w-full text-left p-4 rounded-lg border-2 transition-colors bg-transparent
              {platform === 'xero' ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}"
          >
            <div class="flex items-center justify-between">
              <span class="font-semibold text-gray-900">Xero</span>
              {#if platform === 'xero'}
                <span class="text-blue-600 text-sm font-medium">Selected</span>
              {/if}
            </div>
            <p class="text-sm text-gray-600 mt-1">
              Full two-way sync with Xero — push bills and invoices to Xero and keep paid status in sync.
            </p>
          </button>

          <button
            type="button"
            on:click={() => platform = 'quickbooks'}
            class="w-full text-left p-4 rounded-lg border-2 transition-colors bg-transparent
              {platform === 'quickbooks' ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}"
          >
            <div class="flex items-center justify-between">
              <span class="font-semibold text-gray-900">QuickBooks</span>
              {#if platform === 'quickbooks'}
                <span class="text-blue-600 text-sm font-medium">Selected</span>
              {/if}
            </div>
            <p class="text-sm text-gray-600 mt-1">
              JobTread-only mode — bills import straight to JobTread, which syncs to QuickBooks natively. Xero is disabled.
            </p>
          </button>
        </div>
      </div>

      <div class="px-6 pb-6 flex justify-between">
        <button
          on:click={prevStep}
          class="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium border-0"
        >
          Back
        </button>
        <button
          on:click={nextStep}
          class="px-8 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium border-0"
        >
          Continue
        </button>
      </div>

    <!-- Step: JobTread Grant Setup -->
    {:else if stepKey === 'jobtread'}
      <div class="p-6">
        <h2 class="text-xl font-bold text-gray-900 mb-2">Connect to JobTread</h2>
        <p class="text-gray-600 mb-5">
          In order to connect to Jobtread, we need to create a 'Grant' key which will allow Buildzilla to connect to Jobtread
          and read invoices and bills at your instruction.
        </p>

        <div class="mb-5 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p class="text-sm text-blue-800 mb-2 font-semibold">Setup your JobTread Connection:</p>
          <ol class="text-sm text-blue-800 list-decimal list-inside space-y-1 mb-3">
            <li>Go to <a href="https://app.jobtread.com/settings/integrations/api/grants" target="_blank" class="text-blue-600 underline hover:text-blue-800">API Grants</a></li>
            <li>Click "Add Grant" and name it <strong>Buildzilla</strong></li>
            <li>Copy the grant key and paste it below</li>
            <li>Click "Fetch Organizations" to automatically load your organizations</li>
          </ol>
          <img src="/images/apigrants.jpg" alt="JobTread API Grant Example" class="w-full max-w-sm mx-auto rounded border border-blue-300 shadow-sm" />
        </div>

        <!-- API Key -->
        <div class="mb-4">
          <label for="wiz-jobtreadApiKey" class="block text-sm font-medium text-gray-700 mb-1">
            JobTread Grant Key
          </label>
          {#if jobtreadHasExistingKey && jobtreadApiKey.startsWith('••••••••')}
            <div class="mb-2 p-3 bg-green-50 border border-green-200 rounded-md">
              <p class="text-sm text-green-700">
                A JobTread Grant Key is already saved: <span class="font-mono font-semibold">{jobtreadApiKey}</span>
              </p>
              <p class="text-xs text-green-600 mt-1">To replace it, clear the field and paste a new key.</p>
            </div>
          {/if}
          <input
            type="text"
            id="wiz-jobtreadApiKey"
            bind:value={jobtreadApiKey}
            on:input={() => { availableOrganizations = []; jobtreadOrgId = ''; jobtreadConnectionStatus = null; jobtreadHasExistingKey = false; orgClaimedMessage = null; }}
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder="Paste your grant key here"
          />
          <button
            type="button"
            on:click={fetchOrganizations}
            disabled={fetchingOrganizations || !jobtreadApiKey.trim() || jobtreadApiKey.startsWith('••••••••')}
            class="mt-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed border-0"
          >
            {#if fetchingOrganizations}
              <span class="flex items-center">
                <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Fetching...
              </span>
            {:else}
              Fetch Organizations
            {/if}
          </button>
        </div>

        <!-- Organization dropdown -->
        {#if availableOrganizations.length > 1}
          <div class="mb-4">
            <label for="wiz-jobtreadOrgSelect" class="block text-sm font-medium text-gray-700 mb-1">Select Organization</label>
            <select
              id="wiz-jobtreadOrgSelect"
              bind:value={jobtreadOrgId}
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">-- Select an Organization --</option>
              {#each availableOrganizations as org}
                <option value={org.id}>{org.name}</option>
              {/each}
            </select>
          </div>
        {/if}

        <!-- Manual org ID fallback -->
        {#if showManualOrgId || (jobtreadOrgId && availableOrganizations.length === 0)}
          <div class="mb-4">
            <label for="wiz-jobtreadOrgId" class="block text-sm font-medium text-gray-700 mb-1">Organisation ID (Manual Entry)</label>
            <input
              type="text"
              id="wiz-jobtreadOrgId"
              bind:value={jobtreadOrgId}
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter Organisation ID"
            />
          </div>
        {/if}

        <!-- Org confirmation -->
        {#if jobtreadOrgId && availableOrganizations.length === 1}
          <div class="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
            <p class="text-sm text-green-700">Organization: <strong>{jobtreadOrgName}</strong></p>
          </div>
        {/if}

        <!-- Test connection -->
        <div class="mt-2">
          <button
            type="button"
            on:click={checkJobTreadConnection}
            disabled={jobtreadConnectionStatus === 'checking' || !jobtreadApiKey.trim() || !jobtreadOrgId.trim() || jobtreadApiKey.startsWith('••••••••')}
            class="px-4 py-2 bg-blue-500 text-white text-sm rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed border-0"
          >
            {#if jobtreadConnectionStatus === 'checking'}
              <span class="flex items-center">
                <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Checking...
              </span>
            {:else}
              Check JobTread Connection
            {/if}
          </button>

          {#if jobtreadConnectionStatus === 'success'}
            <div class="mt-2 p-3 bg-green-50 border border-green-200 rounded-md">
              <p class="text-sm text-green-700 font-medium">Connected to: {jobtreadOrgName}</p>
            </div>
          {:else if jobtreadConnectionStatus === 'error'}
            <div class="mt-2 text-sm text-red-600">Connection failed. Please check your credentials.</div>
            {#if orgClaimedMessage}
              <div class="mt-2 p-3 bg-red-50 border border-red-300 rounded-lg">
                <p class="text-sm text-red-800">{orgClaimedMessage}</p>
              </div>
            {/if}
          {/if}
        </div>
      </div>

      <div class="px-6 pb-6 flex justify-between">
        <button
          on:click={prevStep}
          class="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium border-0"
        >
          Back
        </button>
        {#if orgClaimedMessage}
          <button
            on:click={quitOnboarding}
            disabled={closing}
            class="px-6 py-2.5 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed border-0"
          >
            {#if closing}
              <span class="flex items-center">
                <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Exiting...
              </span>
            {:else}
              Exit Setup
            {/if}
          </button>
        {:else}
        <button
          on:click={saveJobTreadSettings}
          disabled={!jobtreadValid || saving}
          class="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed border-0"
        >
          {#if saving}
            <span class="flex items-center">
              <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </span>
          {:else}
            Save JobTread Settings & Continue
          {/if}
        </button>
        {/if}
      </div>

    <!-- Step: Xero Default Accounts (Xero only) -->
    {:else if stepKey === 'xero'}
      <div class="p-6">
        <h2 class="text-xl font-bold text-gray-900 mb-2">Xero Default Accounts</h2>
        <p class="text-gray-600 mb-5">
          Buildzilla provides detailed mapping of Chart of Account codes to Jobtread Cost Codes.
          You can access these in Settings at any time. To get you started, we need to choose the default Xero codes for Expenses and Invoices.
        </p>

        <div class="mb-5 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          {#if useAccountDropdowns}
            <p class="text-sm text-blue-800">
              Select default sales and expense accounts from your Xero Chart of Accounts.
            </p>
          {:else}
            <p class="text-sm text-blue-800 mb-1 font-semibold">Where to find these codes:</p>
            <p class="text-sm text-blue-800">
              In your Xero account, go to Accounting &rarr; Chart of Accounts.
              After connecting Xero you can choose these from a dropdown in Settings.
              <a href="https://xero.com" target="_blank" class="text-blue-600 underline hover:text-blue-800">Open Xero</a>
            </p>
          {/if}
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
          <div>
            <label for="wiz-xeroInvoiceCode" class="block text-sm font-medium text-gray-700 mb-1">
              Default Xero Sales Invoice Code *
            </label>
            {#if useAccountDropdowns}
              <select
                id="wiz-xeroInvoiceCode"
                bind:value={xeroInvoiceCode}
                class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a code</option>
                {#if xeroInvoiceCode.trim() && !salesCodeInList}
                  <option value={xeroInvoiceCode}>
                    {xeroInvoiceCode} (not in sales accounts — please reselect)
                  </option>
                {/if}
                {#each salesAccounts as account}
                  <option value={account.code}>{formatAccountOptionLabel(account)}</option>
                {/each}
              </select>
            {:else if loadingAccounts}
              <div class="flex items-center px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-sm text-gray-600">
                <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                Checking Xero connection…
              </div>
            {:else}
              <input
                type="text"
                id="wiz-xeroInvoiceCode"
                bind:value={xeroInvoiceCode}
                class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., 200"
              />
            {/if}
          </div>
          <div>
            <label for="wiz-xeroExpenseCode" class="block text-sm font-medium text-gray-700 mb-1">
              Default Xero Expense Code *
            </label>
            {#if useAccountDropdowns}
              <select
                id="wiz-xeroExpenseCode"
                bind:value={xeroExpenseCode}
                class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a code</option>
                {#if xeroExpenseCode.trim() && !expenseCodeInList}
                  <option value={xeroExpenseCode}>
                    {xeroExpenseCode} (not in expense accounts — please reselect)
                  </option>
                {/if}
                {#each expenseAccounts as account}
                  <option value={account.code}>{formatAccountOptionLabel(account)}</option>
                {/each}
              </select>
            {:else if loadingAccounts}
              <div class="flex items-center px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-sm text-gray-600">
                <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                Checking Xero connection…
              </div>
            {:else}
              <input
                type="text"
                id="wiz-xeroExpenseCode"
                bind:value={xeroExpenseCode}
                class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., 400"
              />
            {/if}
          </div>
        </div>

        <div>
          <label for="wiz-country" class="block text-sm font-medium text-gray-700 mb-1">Country *</label>
          <select
            id="wiz-country"
            bind:value={country}
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="AU">Australia</option>
            {#if country === 'CA'}<option value="CA">Canada (choose your province)</option>{/if}
            <optgroup label="Canada">
              {#each caProvinceOptions as p}
                <option value={p.value}>{p.label}</option>
              {/each}
            </optgroup>
            <option value="GB">United Kingdom (GBP)</option>
            <option value="GI">Gibraltar (GIP)</option>
            <option value="NZ">New Zealand</option>
            <option value="US">United States</option>
          </select>
        </div>
      </div>

      <div class="px-6 pb-4 flex justify-between">
        <button
          on:click={prevStep}
          class="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium border-0"
        >
          Back
        </button>
        <button
          on:click={saveXeroSettings}
          disabled={!xeroValid || saving}
          class="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed border-0"
        >
          {#if saving}
            <span class="flex items-center">
              <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </span>
          {:else}
            Save Xero Settings & Continue
          {/if}
        </button>
      </div>

      <div class="px-6 pb-6 flex justify-center">
        <button
          on:click={quitOnboarding}
          disabled={closing}
          class="text-xs text-gray-400 hover:text-gray-600 transition-colors border-0 bg-transparent cursor-pointer underline"
        >
          Quit setup — I'll configure this later
        </button>
      </div>

    <!-- Step: Complete -->
    {:else if stepKey === 'complete'}
      <div class="p-6">
        <div class="text-center mb-6">
          <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-50 mb-4">
            <svg class="w-8 h-8 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
          </div>
          <h2 class="text-2xl font-bold text-gray-900 mb-2">Basic Settings Complete!</h2>
        </div>

        {#if platform === 'quickbooks'}
          <div class="space-y-4 mb-6">
            <div class="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p class="text-sm text-gray-700 mb-3">
                You're all set. Buildzilla is configured for <strong>JobTread + QuickBooks</strong>.
              </p>
              <p class="text-sm text-gray-700 mb-3">
                Upload your supplier bills and Buildzilla will extract them and import them straight into JobTread,
                which syncs to QuickBooks natively. No Xero connection is needed.
              </p>
              <p class="text-sm text-gray-700">Cost reporting and direct bill import are ready to go.</p>
            </div>

            <div class="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <p class="text-sm font-semibold text-gray-800 mb-2">To get started, use the menu:</p>
              <div class="ml-2 text-sm text-gray-700">
                <p class="font-medium">Bill Import</p>
                <p class="ml-4 text-blue-700 font-medium">Direct Bill Import</p>
              </div>
            </div>
          </div>
        {:else}
          <div class="space-y-4 mb-6">
            <div class="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p class="text-sm text-gray-700 mb-3">
                Once you close this dialog you will be redirected to <strong>Xero</strong> to connect your Xero account to Buildzilla.
              </p>
              <p class="text-sm text-gray-700 mb-3">
                On the Xero page, choose the correct Xero account and click <strong>Connect</strong>.
              </p>
              <p class="text-sm text-gray-700">
                You will then arrive back in Buildzilla and be ready for your first sync.
              </p>
            </div>

            <div class="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <p class="text-sm font-semibold text-gray-800 mb-2">To get started, use the menu:</p>
              <div class="ml-2 text-sm text-gray-700">
                <p class="font-medium">JobTread to Xero</p>
                <p class="ml-4 text-blue-700 font-medium">Import Bills & Invoices</p>
              </div>
            </div>
          </div>
        {/if}
      </div>

      <div class="px-6 pb-6 flex justify-end">
        <button
          on:click={completeOnboarding}
          disabled={closing}
          class="px-8 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium border-0 disabled:opacity-50"
        >
          {#if closing}
            <span class="flex items-center">
              <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {platform === 'quickbooks' ? 'Loading...' : 'Redirecting to Xero...'}
            </span>
          {:else}
            {platform === 'quickbooks' ? 'Get Started' : 'Connect to Xero & Get Started'}
          {/if}
        </button>
      </div>
    {/if}

    {/if}
  </div>
</div>

<style>
  @keyframes modalFadeIn {
    from { opacity: 0; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
  }
  @keyframes modalFadeOut {
    from { opacity: 1; }
    to { opacity: 0; }
  }
  .animate-fade-in {
    animation: modalFadeIn 0.2s ease-out forwards;
  }
  .fade-out {
    animation: modalFadeOut 0.2s ease-in forwards;
  }
</style>
